import React from 'react';
import {
  Form,
  Navigate,
  useActionData,
  useLoaderData,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
  useNavigation,
  useSearchParams,
  useSubmit
} from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { EyeIcon, NoSymbolIcon, PhoneArrowUpRightIcon } from '@heroicons/react/24/outline';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { DataTable } from '@oysterjs/ui/data-table';
import { useOutboundCallsEnabled } from '~/components/use-outbound-calls-enabled';
import { UsageAllotmentBanner } from '~/components/usage-allotment-banner';
import { SearchFilterInput } from '@oysterjs/ui/search-filter';
import {
  OutboundCallFieldsFragment,
  VoiceAgentOverviewFieldsFragment,
  useGetOutboundCallsRefreshCandidatesQuery,
  useSearchOutboundCallsQuery,
  Action,
  Resource
} from '~/api/graphql';
import { Fieldset } from '@oysterjs/ui/fieldset';
import {
  CREATE_OUTBOUND_CALL,
  PermissionButton,
  PermissionNotice,
  usePermissions
} from '~/components/permissions';
import { ConnectionLoadMoreFooter } from '~/components/connection-load-more-footer';
import {
  getOutboundCallColumns,
  outboundCallRowHref,
  outboundCallSortToOutboundCallSortInput,
  type OutboundCallSortKey
} from '~/components/calls/outbound-calls-columns';
import { useOutboundCallFilters } from '~/api/data/calls/outbound-calls-filters';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import {
  draftFromOutboundCall,
  defaultScheduledTimezone,
  emptyCallDraft,
  formatOutboundCallCreatorLabel,
  getOutboundCallsLiveRefreshPlan,
  isOutboundCallEditable,
  OUTBOUND_CALL_DRAFT_VALIDATION_PATHS,
  OUTBOUND_CALLS_LIVE_POLL_MS,
  OutboundCallDraft,
  outboundCallPrefillFromSearch
} from '~/components/calls/outbound-call';
import { OutboundCallFormDialogFields } from '~/components/calls/outbound-call-form-fields';
import { useOutboundCallsBulkActions } from '~/components/calls/use-outbound-calls-bulk-actions';

const emptyCallDraftForAgents = (agents: VoiceAgentOverviewFieldsFragment[]) =>
  emptyCallDraft(agents[0]?.id ?? '');

const UPDATE_OUTBOUND_CALL = [{ resource: Resource.OutboundCall, action: Action.Update }];
// Marking an attempt seen goes through updateCalls, not the outbound-call
// mutations, so it is a CALL update and not an OUTBOUND_CALL one.
const UPDATE_CALL = [{ resource: Resource.Call, action: Action.Update }];

export const OutboundCallsPage = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      outboundVoiceAgents: VoiceAgentOverviewFieldsFragment[];
    }>;
  };
  const outboundEnabled = useOutboundCallsEnabled();
  if (!outboundEnabled) {
    return <Navigate to="/app/calls/log" replace />;
  }

  return (
    <>
      <UsageAllotmentBanner feature="outboundVoice" className="mb-4" />
      <React.Suspense
        fallback={
          <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
            <div className="text-center py-12">
              <Spinner size="size-8" color="neutral" />
              <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                Loading outbound calls...
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                This might take a moment.
              </p>
            </div>
          </div>
        }
      >
        <Await resolve={loader.data}>
          {(data) => {
            const agents = data?.outboundVoiceAgents ?? [];
            if (agents.length === 0) {
              return (
                <div className="rounded-lg bg-white dark:bg-neutral-800 px-6 py-12 text-center shadow">
                  <PhoneArrowUpRightIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
                  <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                    No outbound voice agents
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Create an outbound voice agent in settings before placing calls.
                  </p>
                  <Button color="sky" className="mt-4" href="/app/settings/voice/new">
                    Create Outbound Agent
                  </Button>
                </div>
              );
            }

            return <OutboundCallsPageContent agents={agents} />;
          }}
        </Await>
      </React.Suspense>
    </>
  );
};

const SEARCH_OUTBOUND_CALLS_PAGE_SIZE = 100;

const OutboundCallsPageContent = ({ agents }: { agents: VoiceAgentOverviewFieldsFragment[] }) => {
  const outlet = useOutlet({ agents });
  const navigation = useNavigation();
  const submit = useSubmit();
  const { filterDefinitions, activeFilters, setActiveFilters, initialFilters, currentFilter } =
    useOutboundCallFilters(agents);
  const [sortKey, setSortKey] = React.useState<OutboundCallSortKey | undefined>('scheduled');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');

  const sortInput = React.useMemo(
    () => outboundCallSortToOutboundCallSortInput(sortKey, sortDirection),
    [sortKey, sortDirection]
  );

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
    fetchMore,
    refetch
  } = useSearchOutboundCallsQuery({
    variables: {
      first: SEARCH_OUTBOUND_CALLS_PAGE_SIZE,
      filter: currentFilter ?? undefined,
      sort: sortInput
    },
    notifyOnNetworkStatusChange: true
  });
  const { data: refreshCandidatesData, refetch: refetchRefreshCandidates } =
    useGetOutboundCallsRefreshCandidatesQuery({
      notifyOnNetworkStatusChange: false
    });

  const connection = searchData?.searchOutboundCalls;
  const calls = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;
  const totalCount = connection?.totalCount ?? 0;

  const isFetchingMoreRef = React.useRef(false);
  const isRefreshingRef = React.useRef(false);
  const loadMore = React.useCallback(() => {
    if (
      !endCursor ||
      !hasNextPage ||
      searchLoading ||
      isFetchingMoreRef.current ||
      isRefreshingRef.current
    ) {
      return;
    }
    isFetchingMoreRef.current = true;
    void fetchMore({
      variables: {
        after: endCursor,
        first: SEARCH_OUTBOUND_CALLS_PAGE_SIZE,
        filter: currentFilter ?? undefined,
        sort: sortInput
      }
    })
      .catch(() => undefined)
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [endCursor, hasNextPage, searchLoading, fetchMore, currentFilter, sortInput]);

  const refreshLoadedPages = React.useCallback(async () => {
    if (isFetchingMoreRef.current || isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;
    try {
      const targetCount = Math.max(calls.length, SEARCH_OUTBOUND_CALLS_PAGE_SIZE);
      const firstPage = await refetch();
      let refreshedCount = firstPage.data?.searchOutboundCalls.edges.length ?? 0;
      let nextCursor = firstPage.data?.searchOutboundCalls.pageInfo.endCursor ?? null;
      let morePages = firstPage.data?.searchOutboundCalls.pageInfo.hasNextPage ?? false;

      while (refreshedCount < targetCount && nextCursor && morePages) {
        const nextPage = await fetchMore({
          variables: {
            after: nextCursor,
            first: SEARCH_OUTBOUND_CALLS_PAGE_SIZE,
            filter: currentFilter ?? undefined,
            sort: sortInput
          }
        });
        const nextConnection = nextPage.data?.searchOutboundCalls;
        refreshedCount += nextConnection?.edges.length ?? 0;
        nextCursor = nextConnection?.pageInfo.endCursor ?? null;
        morePages = nextConnection?.pageInfo.hasNextPage ?? false;
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [calls.length, refetch, fetchMore, currentFilter, sortInput]);

  const refreshListAndPlan = React.useCallback(async () => {
    await Promise.allSettled([refreshLoadedPages(), refetchRefreshCandidates()]);
  }, [refreshLoadedPages, refetchRefreshCandidates]);

  const {
    selection,
    cancellableIds,
    archivableIds,
    cancel,
    archive,
    confirmCancel,
    confirmArchive,
    bulkError,
    dismissBulkError
  } = useOutboundCallsBulkActions({ calls, refetch: refreshListAndPlan });
  const clearSelection = selection.clear;

  const isMarkingAsSeen =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/mark-seen');
  const wasMarkingAsSeenRef = React.useRef(false);
  React.useEffect(() => {
    if (wasMarkingAsSeenRef.current && !isMarkingAsSeen) {
      clearSelection();
      void refreshListAndPlan();
    }
    wasMarkingAsSeenRef.current = isMarkingAsSeen;
  }, [isMarkingAsSeen, refreshListAndPlan, clearSelection]);

  const unseenAttemptIds = React.useMemo(
    () =>
      calls
        .filter(
          (call) => selection.selectedSet.has(call.id) && call.latestCall && !call.latestCall.seenAt
        )
        .map((call) => call.latestCall!.id),
    [calls, selection.selectedSet]
  );

  const markSelectedAsSeen = React.useCallback(() => {
    if (unseenAttemptIds.length === 0) {
      return;
    }
    submit(
      { callIds: unseenAttemptIds, markSeen: true },
      { method: 'POST', action: '/app/calls/outbound/mark-seen', encType: 'application/json' }
    );
  }, [submit, unseenAttemptIds]);

  React.useEffect(() => {
    clearSelection();
  }, [currentFilter, clearSelection]);

  // Stable key so identical status snapshots don't restart timers after each poll.
  const refreshCandidates = React.useMemo(
    () => [
      ...(refreshCandidatesData?.dialing.edges ?? []).map((edge) => edge.node),
      ...(refreshCandidatesData?.inProgress.edges ?? []).map((edge) => edge.node),
      ...(refreshCandidatesData?.scheduled.edges ?? []).map((edge) => edge.node)
    ],
    [refreshCandidatesData]
  );
  const liveRefreshSignature = React.useMemo(
    () =>
      refreshCandidates
        .map((call) => `${call.id}:${call.status}:${call.schedule.scheduledAt ?? ''}`)
        .sort()
        .join('|'),
    [refreshCandidates]
  );
  const refreshCandidatesRef = React.useRef(refreshCandidates);
  refreshCandidatesRef.current = refreshCandidates;

  // Keep status in sync when a scheduled dial starts (and while a call is active).
  React.useEffect(() => {
    const plan = getOutboundCallsLiveRefreshPlan(refreshCandidatesRef.current);
    if (plan.mode === 'idle') {
      return;
    }

    let cancelled = false;
    const refresh = () => {
      if (!cancelled) {
        void refreshListAndPlan();
      }
    };

    if (plan.mode === 'poll') {
      const intervalId = window.setInterval(refresh, plan.intervalMs);
      return () => {
        cancelled = true;
        window.clearInterval(intervalId);
      };
    }

    // After the scheduled time, poll until status leaves SCHEDULED (signature may not
    // change if the row is still overdue-scheduled with the same timestamp).
    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      refresh();
      intervalId = window.setInterval(refresh, OUTBOUND_CALLS_LIVE_POLL_MS);
    }, plan.delayMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [liveRefreshSignature, refreshListAndPlan]);

  // Cancelling and archiving are outbound-call updates; marking seen is a call
  // update. A caller may hold one without the other, so the bar offers whichever
  // of them it can and disappears only when it can offer neither.
  const { hasAllPermissions } = usePermissions();
  const canUpdateOutbound = hasAllPermissions(UPDATE_OUTBOUND_CALL);
  const canMarkSeen = hasAllPermissions(UPDATE_CALL);
  const canSelect = canUpdateOutbound || canMarkSeen;

  const columns = React.useMemo(
    () =>
      getOutboundCallColumns({
        selectedSet: selection.selectedSet,
        setSelectedCalls: selection.setSelected,
        filtered: calls,
        selectable: canSelect
      }),
    [canSelect, selection.selectedSet, selection.setSelected, calls]
  );

  const cancelNoun = cancellableIds.length === 1 ? 'scheduled call' : 'scheduled calls';
  const cancelExtraAction = React.useMemo(
    () => ({
      label: 'Cancel calls',
      onClick: cancel.openDialog,
      disabled: cancellableIds.length === 0,
      loading: cancel.isArchiving,
      icon: <NoSymbolIcon className="size-3.5" />
    }),
    [cancel.isArchiving, cancel.openDialog, cancellableIds.length]
  );

  const markSeenExtraAction = React.useMemo(
    () => ({
      label: 'Mark as Seen',
      onClick: markSelectedAsSeen,
      disabled: unseenAttemptIds.length === 0,
      loading: isMarkingAsSeen,
      icon: <EyeIcon className="size-3.5" />
    }),
    [isMarkingAsSeen, markSelectedAsSeen, unseenAttemptIds.length]
  );

  return (
    <>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by creator, status, voice agent, or my calls..."
          />
        </div>
        <PermissionButton
          permissions={CREATE_OUTBOUND_CALL}
          whenDenied="hide"
          color="sky"
          href="/app/calls/outbound/new"
          className="h-12"
        >
          <PhoneArrowUpRightIcon className="size-4" />
          New Outbound Call
        </PermissionButton>
      </div>

      {bulkError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-900 dark:text-red-200">
          <span>{bulkError}</span>
          <button
            type="button"
            className="shrink-0 font-medium underline"
            onClick={dismissBulkError}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="relative px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
        {canSelect && (
          <FloatingSelectionBar
            count={selection.selected.length}
            onClear={selection.clear}
            extraAction={canUpdateOutbound ? cancelExtraAction : undefined}
            extraActions={canMarkSeen ? [markSeenExtraAction] : []}
            onArchive={archive.openDialog}
            archiveEnabled={canUpdateOutbound && archivableIds.length > 0}
            isArchiving={archive.isArchiving}
          />
        )}
        {searchLoading && calls.length === 0 ? (
          <div className="py-12 text-center">
            <Spinner size="size-8" color="neutral" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Loading outbound calls...
            </h3>
          </div>
        ) : searchError && calls.length === 0 ? (
          <div className="py-12 text-center">
            <PhoneArrowUpRightIcon className="mx-auto h-12 w-12 text-rose-400" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Unable to load outbound calls
            </h3>
            <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{searchError.message}</p>
          </div>
        ) : !calls.length ? (
          <div className="text-center py-12">
            <PhoneArrowUpRightIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              No calls found
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {activeFilters.length > 0
                ? 'No outbound calls match your filters. Try adjusting your criteria.'
                : 'Schedule an outbound call to get started.'}
            </p>
          </div>
        ) : (
          <>
            <DataTable
              data={calls}
              columns={columns}
              keyExtractor={(row) => row.id}
              rowHref={outboundCallRowHref}
              tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={(key, dir) => {
                setSortKey(key);
                setSortDirection(dir);
              }}
            />
            <ConnectionLoadMoreFooter
              shown={calls.length}
              totalCount={totalCount}
              noun="calls"
              hasNextPage={hasNextPage}
              onLoadMore={loadMore}
              loading={searchLoading}
              error={!!searchError}
            />
          </>
        )}
      </div>

      <ArchiveConfirmDialog
        open={cancel.dialogOpen}
        count={cancellableIds.length}
        itemNoun="call"
        title="Cancel calls"
        dismissLabel="Back"
        confirmLabel="Cancel calls"
        loading={cancel.isArchiving}
        description={
          <>
            Are you sure you want to cancel <strong>{cancellableIds.length}</strong> {cancelNoun}?
            They will not be dialed.
          </>
        }
        onCancel={cancel.closeDialog}
        onConfirm={confirmCancel}
      />

      <ArchiveConfirmDialog
        open={archive.dialogOpen}
        count={archivableIds.length}
        itemNoun="call"
        loading={archive.isArchiving}
        description={
          <>
            Are you sure you want to archive <strong>{archivableIds.length}</strong>{' '}
            {archivableIds.length === 1 ? 'call' : 'calls'}? They will be removed from the Calls
            list.
          </>
        }
        onCancel={archive.closeDialog}
        onConfirm={confirmArchive}
      />

      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </>
  );
};

type OutboundCallsOutletContext = {
  agents: VoiceAgentOverviewFieldsFragment[];
};

export type { OutboundCallsOutletContext };

type OutboundCallFormDialogProps = {
  mode: 'create' | 'edit';
  initialDraft: OutboundCallDraft;
  agents: VoiceAgentOverviewFieldsFragment[];
  createdByLabel?: string;
  excludeOutboundCallId?: string;
  onClose: () => void;
};

const OutboundCallFormDialog = ({
  mode,
  initialDraft,
  agents,
  createdByLabel,
  excludeOutboundCallId,
  onClose
}: OutboundCallFormDialogProps) => {
  const submit = useSubmit();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult<unknown> | undefined;
  const [draft, setDraft] = React.useState<OutboundCallDraft>(() =>
    mode === 'create'
      ? { ...initialDraft, scheduledTimezone: defaultScheduledTimezone() }
      : initialDraft
  );
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateFormMulti } = useFormActions(setDraft, setValidationErrors);
  const isSubmitting = navigation.state === 'submitting';

  React.useEffect(() => {
    if (actionData?.validationErrors) {
      setValidationErrors(actionData.validationErrors);
    }
  }, [actionData?.validationErrors]);

  const updateDraft = (patch: Partial<OutboundCallDraft>) => {
    const fieldPaths = (Object.keys(patch) as (keyof OutboundCallDraft)[])
      .map((key) => OUTBOUND_CALL_DRAFT_VALIDATION_PATHS[key])
      .filter((path): path is string[] => !!path);
    updateFormMulti(fieldPaths, (prev) => ({ ...prev, ...patch }));
  };

  const submitDraft = () => {
    submit(draft, { method: 'post', encType: 'application/json' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitDraft();
  };

  const applicationErrorMessage =
    actionData?.applicationError?.message || actionData?.serverError?.message;

  // Reachable by URL, so the dialog checks the permission its mode needs rather
  // than trusting the button that normally opens it.
  const canSave = usePermissions().hasAllPermissions(
    mode === 'create' ? CREATE_OUTBOUND_CALL : UPDATE_OUTBOUND_CALL
  );

  return (
    <MountableDialog onClose={onClose} size="2xl" className="relative">
      <DialogTitle>{mode === 'create' ? 'Place outbound call' : 'Edit scheduled call'}</DialogTitle>
      <DialogDescription>
        {mode === 'create'
          ? 'Configure who to call, when, and what Cara should say. Use the schedule switch to call now or pick a time.'
          : 'Update the call details. Turn off "Schedule for later" to place the call immediately.'}
        {mode === 'edit' && createdByLabel ? <> Created by {createdByLabel}.</> : null}
      </DialogDescription>
      {applicationErrorMessage && (
        <div className="mt-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-900 dark:text-red-200">
          {applicationErrorMessage}
        </div>
      )}
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-8">
          {!canSave && (
            <PermissionNotice>
              You don&rsquo;t have permission to {mode === 'create' ? 'place' : 'change'} outbound
              calls.
            </PermissionNotice>
          )}
          <Fieldset disabled={!canSave}>
            <OutboundCallFormDialogFields
              draft={draft}
              agents={agents}
              onDraftChange={updateDraft}
              validationErrors={validationErrors}
              showSchedule
              excludeOutboundCallId={excludeOutboundCallId}
            />
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {canSave && (
            <Button type="submit" color="sky" disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? draft.scheduleLater
                    ? 'Schedule Call'
                    : 'Place Call'
                  : draft.scheduleLater
                    ? 'Save Changes'
                    : 'Place Call'}
            </Button>
          )}
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const OutboundCallCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agents } = useOutletContext<OutboundCallsOutletContext>();

  return (
    <OutboundCallFormDialog
      mode="create"
      agents={agents}
      initialDraft={{
        ...emptyCallDraftForAgents(agents),
        ...outboundCallPrefillFromSearch(searchParams)
      }}
      onClose={() => navigate('/app/calls/outbound')}
    />
  );
};

export const OutboundCallEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { agents } = useOutletContext<OutboundCallsOutletContext>();
  const loader = useLoaderData() as
    | {
        data: Promise<{ outboundCall: OutboundCallFieldsFragment }>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white dark:bg-neutral-800 p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          const call = data?.outboundCall;
          if (!call) {
            return null;
          }
          if (!isOutboundCallEditable(call.status)) {
            return <Navigate to={`/app/calls/outbound/${id}`} replace />;
          }

          return (
            <OutboundCallFormDialog
              mode="edit"
              agents={agents}
              initialDraft={draftFromOutboundCall(call)}
              createdByLabel={formatOutboundCallCreatorLabel(call.createdBy)}
              excludeOutboundCallId={call.id}
              onClose={() => navigate(`/app/calls/outbound/${id}`, { replace: true })}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};
